import { Component } from '@angular/core';
import { Router } from '@angular/router';
import { NgbModule } from '@ng-bootstrap/ng-bootstrap';
import { TablesService } from '../../services/tables/tables.service';
import { TableInfo } from '../../services/tables/interfaces/table.interface';
import { CommonModule } from '@angular/common';
import { LoadingSpinnerComponent } from '../../components/loading-spinner/loading-spinner.component';
import { OrderCardComponent } from '../../ui/molecules/order-card/order-card.component';
import { finalize, catchError, switchMap, map } from 'rxjs/operators';
import { forkJoin, of, Observable, Subscription } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { CustomerNameDialogComponent } from './customer-name-dialog.component';
import { TableEditDialogComponent } from './table-edit-dialog.component';
import { ConfirmDialogComponent } from '../../shared/confirm-dialog/confirm-dialog.component';
import { ConfigService } from '../../services/config.service';
import { KeycloakService } from 'keycloak-angular';

interface OrderDetailsResponse {
  items: Array<{
    uuid?: string;
    name: string;
    price: number;
    qty: number;
    itemStatusSlug?: string;
    status?: string | { slug: string };
  }>;
  // Add other order details fields as needed
}

@Component({
  selector: 'app-tables-list',
  standalone: true,
  // Use global providers to ensure Http interceptors are applied
  providers: [],
  imports: [
    NgbModule, 
    CommonModule, 
    LoadingSpinnerComponent, 
    OrderCardComponent,
    MatIconModule,
    MatButtonModule,
    MatTooltipModule,
    MatDialogModule,
    MatSnackBarModule,
    
  ],
  templateUrl: './tables.component.html',
  styleUrl: './tables.component.css'
})
export class TablesListComponent {
  isLoading = false;
  // Tabs: 0=ALL (Todas), 1=IDLE (Disponibles), 2=OPEN (Abiertas), 4=PAYMENT_PENDING (Por pagar), 3=CLOSED (Cerradas)
  active = 0;
  // Type Tabs: ALL | LOCAL | DELIVERY | PICKUP
  activeType: 'ALL' | 'LOCAL' | 'DELIVERY' | 'PICKUP' | 'FOODTRUCK' = 'LOCAL';
  private readonly LS_KEYS = {
    tableType: 'ui.activeTableType'
  } as const;
  tables: TableInfo[] = [];
  availableStatuses: string[] = [];
  private orderDetailsCache = new Map<string, OrderDetailsResponse>();
  isRefreshing = false;
  foodtruckOnly = false;
  private subscription: Subscription | null = null;
  private deleting = new Set<string>();

  constructor(
    private router: Router, 
    private tableService: TablesService,
    private http: HttpClient,
    private dialog: MatDialog,
    private snack: MatSnackBar,
    private config: ConfigService,
    private keycloak: KeycloakService,
  ) { }

  ngOnInit() {
    this.updateFoodtruckFlag();
    // Restaurar tipo desde localStorage para sincronizar con Órdenes
    try {
      const savedType = localStorage.getItem(this.LS_KEYS.tableType);
      if (savedType === 'ALL' || savedType === 'LOCAL' || savedType === 'DELIVERY' || savedType === 'PICKUP' || savedType === 'FOODTRUCK') {
        this.activeType = savedType as typeof this.activeType;
      }
    } catch {}
    if (this.foodtruckOnly) {
      this.activeType = 'FOODTRUCK';
    }
    this.loadData();
  }

  ngOnDestroy() {
    if (this.subscription) {
      this.subscription.unsubscribe();
      this.subscription = null;
    }
  }

  refreshData(): void {
    if (this.isRefreshing) return;
    
    this.isRefreshing = true;
    this.loadData();
  }
  isDeleting(uuid: string): boolean { return this.deleting.has(uuid); }
  
  private loadData(): void {
    this.isLoading = true;
    this.tables = [];
    
    const statuses$ = this.tableService.getAvailableStatuses().pipe(catchError(() => of([])));
    const tables$ = this.tableService.getTables().pipe(
      catchError(() => of([])),
      map((tables: TableInfo[]) => {
        // Usar únicamente los datos devueltos por getTables(), sin llamar a order-detail
        return (tables || []).map((table: any) => {
          // Normalizar items desde distintas ubicaciones posibles
          let rawItems: any[] = [];
          if (Array.isArray(table?.items)) rawItems = table.items;
          else if (Array.isArray(table?.order?.items)) rawItems = table.order.items;
          else if (Array.isArray(table?.orderDetail)) rawItems = table.orderDetail;

          const items = (rawItems || []).map((raw: any) => {
            const baseQty = Number(raw?.qty ?? raw?.quantity) || 0;
            const priceBase = typeof raw?.price === 'number' ? raw.price
              : (typeof raw?.product?.sellPrice === 'number' ? raw.product.sellPrice
              : (typeof raw?.menuProduct?.sellPrice === 'number' ? raw.menuProduct.sellPrice : 0));
            const itemStatusSlug = (raw?.itemStatusSlug
              || (typeof raw?.status === 'string' ? raw.status : raw?.status?.slug)
              || 'pending');
            const name = (
              raw?.name
              ?? raw?.product?.name
              ?? raw?.menuProduct?.name
              ?? raw?.productName
              ?? raw?.menuProductName
              ?? raw?.product?.title
              ?? raw?.menuProduct?.title
              ?? ''
            );

            // Opciones seleccionadas: nuevo contrato selectedOptions + compatibilidad legacy
            const rawOptions: any[] = Array.isArray(raw?.selectedOptions)
              ? raw.selectedOptions
              : (Array.isArray(raw?.optionSnapshots)
                ? raw.optionSnapshots
                : (Array.isArray(raw?.options) ? raw.options : []));
            // Cantidad agregada: usar conteo de opciones si existe, o campos agregados del backend
            const qtyAgg: number = Number(
              raw?.selectedOptionsCount ?? raw?.totalItems ?? ((rawOptions && rawOptions.length > 0) ? rawOptions.length : baseQty)
            ) || 0;
            // Total de línea: preferir total agregado de opciones si viene
            const lineTotal: number = Number(
              raw?.selectedOptionsSellPriceTotal ?? raw?.totalSellPrice ?? (priceBase * qtyAgg)
            ) || 0;

            // Desglose de opciones: colapsar por nombre con conteos
            const optionsMap = new Map<string, number>();
            (rawOptions || []).forEach((opt: any) => {
              const oname = String(opt?.name ?? opt?.optionName ?? opt?.menuProductOption?.name ?? '').trim();
              if (!oname) return;
              const oq = Number(opt?.qty ?? opt?.quantity ?? 1) || 1;
              optionsMap.set(oname, (optionsMap.get(oname) || 0) + oq);
            });
            const optionsList = Array.from(optionsMap.entries()).map(([oname, oq]) => ({ name: oname, qty: oq }));

            return {
              ...raw,
              name,
              // Mostrar n × Opción bajo el nombre
              options: optionsList,
              // Cantidad agregada para la fila
              qty: qtyAgg,
              // Precio base (para fallback) y total de línea (para mostrar)
              price: priceBase,
              lineTotal,
              itemStatusSlug,
              status: itemStatusSlug
            };
          });

          // Datos de pago/resumen
          const pay = (table?.payment ?? table?.order?.payment) as any;
          let total: number = Number(pay?.itemsSubtotal ?? table?.total ?? 0);
          if (!Number.isFinite(total) || total <= 0) {
            total = (items || []).reduce((sum: number, it: any) => {
              const p = Number(it?.price) || 0;
              const q = Number(it?.qty ?? it?.quantity) || 0;
              return sum + p * q;
            }, 0);
          }

          // Construir resumen de precios compatible con app-order-card
          const discount = Number(pay?.discount ?? (table as any)?.discountAmount ?? 0);
          const tip = Number(pay?.tip ?? (table as any)?.tipAmount ?? 0);
          const priceSummary = {
            total: Number(total || 0),
            discount,
            tip,
            payable: Number(pay?.totalDue ?? (Number(total || 0) - discount + tip))
          };

          // Resumen de pago para el footer
          const paid = Number(pay?.paidTotal ?? 0);
          const pending = Number(pay?.remaining ?? (priceSummary.payable - paid));
          const methodsArr: string[] = ((table?.payments ?? table?.order?.payments) || []).map((p: any) => p?.paymentMethod).filter(Boolean);
          const paymentSummary = { paid, pending, methods: methodsArr };

          // Derivar id corto de orden si viene
          const orderId = (table?.order?.id ?? table?.orderId ?? table?.orderHashId ?? (table as any)?.id) ?? '';
          // Derivar/normalizar status si viene en distintos campos
          const rawStatus = (table?.status ?? table?.order?.status ?? (table as any)?.statusName ?? (table as any)?.statusSlug) as string | undefined;
          const status = this.normalizeStatus(rawStatus);
          // Normalizar nombre del cliente: puede venir como string u objeto
          let customerName: string = '';
          const rawClient: any = (table as any)?.clientName ?? (table as any)?.customerName ?? (table as any)?.client ?? (table as any)?.customer;
          if (typeof rawClient === 'string') {
            customerName = rawClient;
          } else if (rawClient && typeof rawClient === 'object') {
            customerName = (rawClient.name ?? rawClient.fullName ?? rawClient.title ?? '');
          }
          customerName = (customerName || '').toString().trim();

          return { ...table, items, total, orderId, status, customerName, priceSummary, paymentSummary } as TableInfo as any;
        });
      })
    );

    if (this.subscription) {
      this.subscription.unsubscribe();
    }

    this.subscription = forkJoin({ statuses: statuses$, tables: tables$ })
      .pipe(
        finalize(() => {
          this.isLoading = false;
          this.isRefreshing = false;
        })
      )
      .subscribe({
        next: ({ statuses, tables }) => {
          const rawSlugs = (statuses || []).map((o: any) => o?.slug || o?.name);
          const toCanonical = (val: string): string => {
            if (!val) return '';
            const s = String(val).toLowerCase();
            if (s.includes('nuevo') || s.includes('idle')) return 'IDLE';
            return String(val).toUpperCase();
          };
          this.availableStatuses = (rawSlugs || []).map(toCanonical);
          console.log('[Tables] Available statuses RAW ->', rawSlugs, ' MAPPED ->', this.availableStatuses);
          if (!this.availableStatuses || this.availableStatuses.length === 0) {
            this.availableStatuses = ['OPEN', 'IDLE', 'PAYMENT_PENDING', 'CLOSED'];
          }

          // Por defecto, mostrar 'Todas'
          this.active = 0;

          const normalized: TableInfo[] = (tables || []).map((t: TableInfo) => this.normalizeTable(t));
          console.group('[Tables] Merge result');
          normalized.forEach((t: any) => {
            const len = Array.isArray((t as any).items) ? (t as any).items.length : 0;
            console.log(`Table ${t?.uuid} (${t?.name}) -> items: ${len}, total: ${t?.total ?? 0}`);
          });
          console.groupEnd();
          let list = normalized
            .map((t: any) => ({ ...t }))
            .sort((a: TableInfo, b: TableInfo) => {
              const na = a?.number ?? Number.MAX_SAFE_INTEGER;
              const nb = b?.number ?? Number.MAX_SAFE_INTEGER;
              return na - nb;
            });
          if (this.foodtruckOnly) {
            list = list.filter(t => (String((t as any).type || '').toUpperCase() === 'FOODTRUCK'));
          }
          this.tables = list;
        },
        error: (err) => {
          console.error('[Tables] loadData error', err);
        }
      });
  }
  onSelectTable(uuid: string) {
    const tableSelected = this.tables.find(table => table.uuid === uuid);
    console.log({ tableSelected });
    const typeNorm = String((tableSelected as any)?.type || '').toUpperCase();
    if (typeNorm === 'FOODTRUCK') {
      const queryParams: any = {};
      const existingOrder = (tableSelected as any)?.orderHashId;
      if (existingOrder) queryParams.orderHashId = existingOrder;
      this.router.navigate([`/admin/foodtruck/${tableSelected?.uuid}`], { queryParams });
      return;
    }
    const statusNorm = this.normalizeStatus(tableSelected?.status);
    if (statusNorm === 'IDLE') {
      // Directo a solicitar nombre de cliente para nueva orden
      const dialogRef = this.dialog.open(CustomerNameDialogComponent, {
        width: '320px',
        data: { defaultName: tableSelected?.customerName || '' }
      });
      dialogRef.afterClosed().subscribe((result?: { name: string; createdAt?: Date }) => {
        if (result === undefined) return; // cancelado
        const clean = this.normalizeClientName(result?.name);
        const queryParams: any = clean ? { customer: clean } : {};
        this.router.navigate(['/admin/orders-new/', tableSelected?.uuid], { queryParams });
      });
    } else if (statusNorm === 'CLOSED') {
      const label = (tableSelected?.name || (tableSelected?.number != null ? `Mesa ${tableSelected?.number}` : 'mesa')).toString();
      // Advertencia: abrir mesa cerrada creará una nueva orden
      const confirmRef = this.dialog.open(ConfirmDialogComponent, {
        width: '320px',
        data: {
          title: 'Confirmación',
          message: `¿Deseas abrir la ${label} y generar una nueva orden?`,
          cancelLabel: 'Cancelar',
          confirmLabel: 'Aceptar'
        }
      });
      confirmRef.afterClosed().subscribe((proceed?: boolean) => {
        if (!proceed) return; // usuario canceló
        const dialogRef = this.dialog.open(CustomerNameDialogComponent, {
          width: '320px',
          data: { defaultName: '' }
        });
        dialogRef.afterClosed().subscribe((result?: { name: string; createdAt?: Date }) => {
          if (result === undefined) return; // cancelado
          const clean = this.normalizeClientName(result?.name);
          const queryParams: any = clean ? { customer: clean } : {};
          this.router.navigate(['/admin/orders-new/', tableSelected?.uuid], { queryParams });
        });
      });
    } else {
      this.router.navigate([`/admin/orders-edit/${tableSelected?.uuid}/${tableSelected?.orderHashId}`]);
    }
  }

  // Use arrow property to ensure template type-checker sees the member
  onEditTable = (uuid: string) => {
    try {
      const table = this.tables.find(t => t.uuid === uuid);
      if (!table) return;
      const ref = this.dialog.open(TableEditDialogComponent, {
        data: {
          table,
          types: ['LOCAL', 'DELIVERY', 'PICKUP', 'FOODTRUCK']
        }
      });
      ref.afterClosed().subscribe((result: Partial<TableInfo> | null) => {
        if (!result) return; // cancelado
        const cleanName = (result.name ?? table.name ?? '').toString().trim();
        const enabled = result.enabled ?? (table.enabled !== false);
        const type = (result.type as any) ?? table.type ?? 'LOCAL';
        const payload: TableInfo = { ...table, name: cleanName, enabled, type } as TableInfo;
        this.tableService.updateTable(payload).subscribe({
          next: () => {
            this.tables = this.tables.map(t => t.uuid === uuid ? { ...t, name: cleanName, enabled, type } : t);
            this.snack.open('Mesa actualizada', 'Cerrar', { duration: 2500 });
          },
          error: (err) => {
            console.error('[Tables] Error al editar mesa', err);
            this.snack.open('No se pudo actualizar la mesa', 'Cerrar', { duration: 3500 });
          }
        });
      });
    } catch (e) {
      console.error('[Tables] onEditTable error', e);
    }
  }

  // Use arrow property to ensure template type-checker sees the member
  onDeleteTable = (uuid: string) => {
    const table = this.tables.find(t => t.uuid === uuid);
    const name = table?.name || 'la mesa';

    const ref = this.dialog.open(ConfirmDialogComponent, {
      data: {
        title: 'Borrar mesa',
        message: `Se eliminará "${name}". Esta acción no se puede deshacer.`,
        cancelLabel: 'Cancelar',
        confirmLabel: 'Borrar',
        confirmColor: 'warn'
      }
    });
    ref.afterClosed().subscribe((ok: boolean) => {
      if (!ok) return;
      this.deleting.add(uuid);
      this.tableService.deleteTable(uuid)
        .pipe(finalize(() => this.deleting.delete(uuid)))
        .subscribe({
          next: () => {
            // Espera respuesta del backend y refresca la lista desde servidor
            this.snack.open('Mesa eliminada', 'Cerrar', { duration: 2500 });
            this.refreshData();
          },
          error: (err) => {
            console.error('[Tables] delete error', err);
            this.snack.open('No se pudo eliminar la mesa', 'Cerrar', { duration: 3500 });
          }
        });
    });
  }

  // Old soft-delete handler removed in favor of confirm + DELETE API
  setActive(val: number) {
    this.active = val;
  }

  private updateFoodtruckFlag(): void {
    try {
      const rolesA = (this.keycloak?.getUserRoles ? this.keycloak.getUserRoles() : []) as string[];
      const rolesB = (this.keycloak?.getUserRoles ? this.keycloak.getUserRoles(true) : []) as string[];
      const all = [...new Set([...(rolesA || []), ...(rolesB || [])].map(r => (r || '').toLowerCase()))];
      let hasAdmin = all.includes('admin');
      const inst: any = (this.keycloak as any)?.getKeycloakInstance?.();
      if (inst?.hasRealmRole && inst.hasRealmRole('admin')) hasAdmin = true;
      if (inst?.hasResourceRole && inst.hasResourceRole('admin')) hasAdmin = true;
      const isAdmin = !!hasAdmin;
      this.foodtruckOnly = this.config.foodtruckOnly && !isAdmin;
    } catch {
      this.foodtruckOnly = this.config.foodtruckOnly;
    }
  }

  setActiveType(val: 'ALL' | 'LOCAL' | 'DELIVERY' | 'PICKUP' | 'FOODTRUCK') {
    if (this.foodtruckOnly && val !== 'FOODTRUCK') return;
    this.activeType = val;
    try { localStorage.setItem(this.LS_KEYS.tableType, this.activeType); } catch {}
  }
  normalizeStatus(status: string | undefined): string {
    if (!status) return 'IDLE';
    const s = status.toLowerCase().trim();
    if (s === 'abierta' || s === 'open' || s === 'abierto') return 'OPEN';
    if (s === 'cerrada' || s === 'closed' || s === 'cerrado') return 'CLOSED';
    if (s === 'por pagar' || s === 'payment_pending' || s === 'pending_payment') return 'PAYMENT_PENDING';
    if (s === 'disponible' || s === 'idle' || s === 'available') return 'IDLE';
    // If we get here, return the original status in uppercase for consistency
    return status.toUpperCase();
  }

  filterTable(table: any): boolean {
    if (this.active === 0) return true; // Todas
    const status = this.normalizeStatus(table.status);
    if (this.active === 1 && status === 'IDLE') return true;
    if (this.active === 2 && status === 'OPEN') return true;
    if (this.active === 3 && status === 'CLOSED') return true;
    if (this.active === 4 && status === 'PAYMENT_PENDING') return true;
    return false;
  }

  passesFilters(table: TableInfo): boolean {
    if (!this.filterTable(table)) return false;
    if (this.activeType === 'ALL') return true;
    return (table.type || 'LOCAL') === this.activeType;
  }

  countByType(type: 'ALL' | 'LOCAL' | 'DELIVERY' | 'PICKUP' | 'FOODTRUCK'): number {
    // counts constrained by current status tab selection
    if (type === 'ALL') return this.tables.filter(t => this.filterTable(t)).length;
    return this.tables.filter(t => this.filterTable(t) && ((t.type || 'LOCAL') === type)).length;
  }

  countBy(status: string): number {
    return this.tables.filter(t => {
      const matchesStatus = this.normalizeStatus(t.status) === status;
      const matchesType = (this.activeType === 'ALL') || ((t.type || 'LOCAL') === this.activeType);
      return matchesStatus && matchesType;
    }).length;
  }

  countAll(): number {
    return this.tables.filter(t => {
      const matchesType = (this.activeType === 'ALL') || ((t.type || 'LOCAL') === this.activeType);
      return matchesType;
    }).length;
  }

  countOpenLike(): number {
    return this.tables.filter(t => {
      const matchesStatus = this.normalizeStatus(t.status) === 'OPEN';
      const matchesType = (this.activeType === 'ALL') || ((t.type || 'LOCAL') === this.activeType);
      return matchesStatus && matchesType;
    }).length;
  }

  // Show all status tabs regardless of backend-provided availability
  hasOpen(): boolean { return true; }
  hasPaymentPending(): boolean { return true; }
  hasClosed(): boolean { return true; }
  hasIdle(): boolean { return true; }

  getTableClass(status: string): string {
    switch (status) {
      case 'OPEN': return 'bg-open';
      case 'DELAYED': return 'bg-delayed';
      case 'PAYMENT_PENDING': return 'bg-payment';
      default: return '';
    }
  }

  private normalizeClientName(raw: any): string {
    if (typeof raw === 'string') return raw.trim();
    if (raw && typeof raw === 'object') {
      const candidates = [raw.name, raw.firstName, raw.lastName, raw.customerName, raw.fullName, raw.displayName];
      const found = candidates.find(v => typeof v === 'string' && v.trim().length > 0);
      return found ? found.trim() : '';
    }
    if (raw == null) return '';
    return String(raw).trim();
  }

  private normalizeTable(table: TableInfo): TableInfo {
    // Normalize type
    let type: 'LOCAL' | 'DELIVERY' | 'PICKUP' | 'FOODTRUCK' | undefined = table.type as any;
    if (type) {
      type = String(type).toUpperCase() as any;
    } else {
      const lname = (table.name || '').toLowerCase();
      if (lname.includes('pickup')) type = 'PICKUP';
      else if (lname.includes('delivery') || lname.includes('envio') || lname.includes('reparto')) type = 'DELIVERY';
      else if (lname.includes('foodtruck') || lname.includes('food-truck') || lname.includes('food truck')) type = 'FOODTRUCK';
      else type = 'LOCAL';
    }

    // Normalize/infer number
    let number = table.number;
    if (number === undefined || number === null) {
      const m = (table.name || '').match(/(\d+)/);
      if (m) number = parseInt(m[1], 10);
      else if ((table as any).id !== undefined) number = (table as any).id as any;
    }

    // Normalize status
    let status = this.normalizeStatus((table as any).status as string | undefined);
    if (!status || status.trim() === '') {
      status = (table as any).orderHashId ? 'OPEN' : 'IDLE';
    }

    return { ...table, type, number, status } as TableInfo;
  }


}

